/**
 * PATH: apps/qa/src/pages/records-list/useQaJobStatusSelection.ts
 * FILE_ID: QA-RECORDS-JOB-STATUS-001
 * PURPOSE: Jobs status selection and portfolio counts for the QA home.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { ComponentProps } from 'react'
import { JobsStatusFilterControl } from '@quotech/job-hub/jobs-list/filters/JobsStatusFilterControl'
import api from '@/lib/api'

export type JobStatusKey = 'tbc' | 'wip' | 'done' | 'bin'

type FacetOption = { value: string; label: string; count: number }
type JobStatusAuthority = ComponentProps<typeof JobsStatusFilterControl>['authority']

function facetOptions(payload: unknown): FacetOption[] {
  const raw = (payload as { status_buckets?: unknown } | null)?.status_buckets
  if (!Array.isArray(raw)) return []
  return raw.flatMap((item) => {
    if (!item || typeof item !== 'object') return []
    const row = item as { value?: unknown; label?: unknown; count?: unknown }
    if (typeof row.value !== 'string' || typeof row.label !== 'string') return []
    const count = Number(row.count)
    return [{ value: row.value, label: row.label, count: Number.isFinite(count) ? count : 0 }]
  })
}

export function useQaJobStatusSelection(
  organisationId: string | number | null | undefined,
  enabled: boolean,
  setPage: (page: number) => void,
) {
  const [jobStatusBuckets, setJobStatusBuckets] = useState<JobStatusKey[]>(['wip'])
  // Reused from: apps/qa/src/pages/RecordsPage.tsx (GET /v2/jobs/)
  // and report/views/jobs_v2.py (JobsV2FacetsAPI.get).
  const facets = useQuery({
    queryKey: ['qa-job-status-facets', organisationId],
    enabled: Boolean(enabled && organisationId),
    queryFn: async () => {
      const { data } = await api().get('/v2/jobs/facets/', {
        params: { tenant: organisationId },
      })
      return facetOptions(data)
    },
  })
  const source: JobStatusAuthority['source'] = facets.isError
    ? 'fallback'
    : facets.isSuccess
      ? 'authoritative'
      : 'pending'
  const authority: JobStatusAuthority = {
    source,
    value: {
      total: 0,
      status_buckets: facets.data ?? [],
      managers: [],
      stages: [],
      enum_options: { status: [], combined_status: [], flags: [] },
    },
  }

  function onToggleJobStatus(key: JobStatusKey) {
    setJobStatusBuckets((current) => (
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
    ))
    setPage(1)
  }

  return {
    jobStatusBuckets,
    onToggleJobStatus,
    authority,
    jobStatusActive: jobStatusBuckets.length > 0 && jobStatusBuckets.length < 4,
  }
}
